import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type CreateMemberRequest, CreateMemberRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// frequent-customer-memberships · Slice S2a — Member & Vehicle Identity
// (spec Capability 2). Plates are entered as one comma-separated field
// (rather than a react-hook-form field array) to keep this form compact —
// there is no precedent for `useFieldArray` in this codebase yet, and a
// member is typically enrolled with 1-2 plates.

const FormValuesSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  platesInput: z.string(),
});
type FormValues = z.infer<typeof FormValuesSchema>;

interface MemberFormProps {
  onSubmit: (values: CreateMemberRequest) => Promise<void>;
  submitLabel?: string;
  onCancel?: () => void;
}

function parsePlates(raw: string): string[] {
  return raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

export function MemberForm({ onSubmit, submitLabel, onCancel }: MemberFormProps) {
  const { t } = useTranslation('memberships');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormValuesSchema),
    defaultValues: { name: '', email: '', phone: '', platesInput: '' },
  });

  async function handleFormSubmit(values: FormValues) {
    setServerError(null);
    const candidate = {
      name: values.name,
      email: values.email,
      phone: values.phone?.trim() ? values.phone.trim() : undefined,
      plates: parsePlates(values.platesInput),
    };
    const parsed = CreateMemberRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      setServerError(parsed.error.issues[0]?.message ?? t('memberForm.invalidInput'));
      return;
    }
    try {
      await onSubmit(parsed.data);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('memberForm.genericError') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('memberForm.fullName.label')} htmlFor="name" error={errors.name?.message}>
          <Input
            id="name"
            placeholder={t('memberForm.fullName.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>

        <Field label={t('memberForm.email.label')} htmlFor="email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            placeholder={t('memberForm.email.placeholder')}
            disabled={isSubmitting}
            {...register('email')}
          />
        </Field>

        <Field label={t('memberForm.phone.label')} htmlFor="phone" error={errors.phone?.message}>
          <Input
            id="phone"
            placeholder={t('memberForm.phone.placeholder')}
            disabled={isSubmitting}
            {...register('phone')}
          />
        </Field>

        <Field
          label={t('memberForm.plates.label')}
          htmlFor="platesInput"
          error={errors.platesInput?.message}
        >
          <Input
            id="platesInput"
            placeholder={t('memberForm.plates.placeholder')}
            disabled={isSubmitting}
            {...register('platesInput')}
          />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('memberForm.saving') : (submitLabel ?? t('memberForm.createMember'))}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
